import { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/router'
import { Button, Spinner, Form } from 'react-bootstrap'
import Head from 'next/head'
import { AuthServices, UserServices } from 'services'
import { connect } from 'react-redux'
import Cookies from 'js-cookie'
import { loginSuccess, logout, setAccessToken } from 'actions'
import { toast } from 'components/common/Toast'
import { LoadingOverlay } from 'components/common'
import { AppConfigServices } from 'services/index'
import PropTypes from 'prop-types'
import config from 'config'
import { AppleSignIn, GoogleLogin, PublicLanguage, Select } from 'components/common/index'
import nextI18NextConfig from '../../i18n'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { YandexLogin } from 'components/common/index'
import { Facebook, Yandex, At } from 'assets/icons/components'
import { MailruLogin } from 'components/common/index'
import HCaptcha from '@hcaptcha/react-hcaptcha';

let logo = '/images/logo.png'

const Register = (props) => {
  const { dispatch, accessToken, locale } = props
  const { t } = useTranslation('register')
  const [isLoadingProvider, setIsLoadingProvider] = useState(false)
  const [isLoadingForm, setIsLoadingForm] = useState(false)
  const [isPageLoad, setIsPageLoad] = useState(false)
  const [inputValues, setInputValues] = useState({
    username: '', password: '', ages: null, name: ''
  })
  const [registerStep, setRegisterStep] = useState(0)
  const [isShowCaptcha, setIsShowCaptcha] = useState(false)
  const captchaRef = useRef(null)

  const router = useRouter()

  useEffect(() => {
    getCurrentUser()
  }, [])

  const getCurrentUser = async () => {
    if (Cookies.get('accessToken')) {
      const { user, error } = await UserServices.getProfile()
      setIsPageLoad(false)

      if (user) {
        dispatch(loginSuccess(user, accessToken))

        if (user?.gender_verified) {
          router.push('/live')
        } else {
          router.push('/verify-gender')
        }
      } else {
        Cookies.remove('accessToken')
      }
    } else {
      setIsPageLoad(false)
    }
  }

  const handleOnChange = event => {
    const value = event.target.value
    const id = event.target.id
    setInputValues({ ...inputValues, [id]: value })
  }

  const handleSocialLogin = async (provider, accessToken) => {
    if (accessToken) {
      setIsLoadingForm(true)

      const { token, error, is_new } = await AuthServices.socialGoogle({ provider: provider, ...accessToken })

      if (token) {
        dispatch(setAccessToken(token))
        Cookies.set('accessToken', token, { expires: 7 })
        window.dataLayer && dataLayer.push({ event: 'register' })
        if (is_new) {
          Cookies.get('gclid') && window.gtag && gtag('event', 'conversion', { send_to: 'AW-17596970317/2sRbCNG24cocEM3q8cZB' })
        }
        router.push('/live')
      }

      setIsLoadingForm(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoadingForm(true)

    if (inputValues.ages === null || inputValues.ages === "") {
      toast.error(t('Please select your age'))
      setIsLoadingForm(false)
      return;
    }

    inputValues.email = inputValues.username
    const { token, error } = await AuthServices.register(inputValues)

    if (token) {
      dispatch(setAccessToken(token))
      Cookies.set('accessToken', token, { expires: 7 })
      window.dataLayer && dataLayer.push({ event: 'register' })
      Cookies.get('gclid') && window.gtag && gtag('event', 'conversion', { send_to: 'AW-17596970317/2sRbCNG24cocEM3q8cZB' })
      router.push('/live')
    } else {
      let errors = error.response?.data?.errors

      if (errors?.token) {
        setIsShowCaptcha(true)
      }

      if (errors) {
        toast.error(errors[Object.keys(errors)[0]][0])
      } else {
        toast.error(t('Something was wrong. Please try again.'))
      }
    }

    setIsLoadingForm(false)
  }

  const backStep = () => {
    setIsLoadingForm(false)
    if (registerStep > 0) {
      setRegisterStep(registerStep - 1)
    }
  }

  if (isPageLoad) {
    return <LoadingOverlay />
  }

  return (
    <div className='login-container'>
      <Head>
        <title>Connectmingle | Register</title>
        {/* SEO page */}
        <meta name='description' content={t('description')} />
        <meta property='og:url' content={ config.URL + router.route } />
        <meta property='og:title' content='Connectmingle | Register' />
        <meta property='og:image' content={ config.URL + '/images/logo.png' } />
        <meta property='og:description' content={t('description')} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Connectmingle" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content='Connectmingle | Register' />
        <meta name="twitter:description" content={t('description')} />
        <meta name="twitter:image" content={ config.URL + '/images/logo.png' } />
        <meta name="twitter:url" content={ config.URL + router.route } />
        <meta name="robots" content="noindex" />
      </Head>
      <div className='login-container__login-wrapper'>
        <div className='login-wrapper__logo'>
          <Link href='/'>
            <img src={logo} className='logo__image' />
          </Link>
          <div className="content__locales">
            <PublicLanguage t={t} locale={locale} path="/register"/>
          </div>
        </div>
        
        {registerStep === 0 ?
          <>
            <Form className='login-wrapper__form' onSubmit={() => setRegisterStep(1)}>
              <Form.Group controlId='username'>
                <Form.Label>{t('form.input-1.label')}:</Form.Label>
                <Form.Control
                  onChange={handleOnChange}
                  value={inputValues.username}
                  type='email'
                  required
                  autoComplete='current-text'
                  placeholder={t('form.input-1.placeholder')} />
              </Form.Group>
              <Form.Group controlId='password'>
                <Form.Label>{t('form.input-2.label')}:</Form.Label>
                <Form.Control
                  onChange={handleOnChange}
                  value={inputValues.password}
                  type='password'
                  required
                  placeholder={t('form.input-2.placeholder')}
                  autoComplete='current-password' />
              </Form.Group>
              <Button
                variant='primary'
                type='submit'
                disabled={isLoadingForm || isLoadingProvider}
                size='lg'
                className='form__button'>
                {isLoadingForm ? <><Spinner
                  as='span'
                  animation='border'
                  size='sm'
                  style={{ marginBottom: '2px' }}
                  role='status'
                /> {t('form.continue')}</> : <>{t('form.continue')}</>}
              </Button>
              <div className='form__account-links'>
                <div className='account-links__sign'>
                  {t('have-an-account')}{' '}
                  <Link href='/login'>
                    <a style={{ color: '#ec0043' }}> {t('login')}</a>
                  </Link>
                </div>
              </div>
            </Form>
            <div className='login-wrapper__break-wrapper'>
              <div className='break-wrapper__text'>
                {t('or')}
              </div>
              <div className='break-wrapper__break'></div>
            </div>
            <div className='login-wrapper__login-buttons'>
              <GoogleLogin onSuccess={(accessToken) => handleSocialLogin('google', accessToken)} />
              <AppleSignIn
                onSuccess={handleSocialLogin}
                style={{ marginLeft: '15px' }}
              />
            </div>
            <div className='login-wrapper__logo'>
              <Link href='/trial'>
                <a style={{ color: '#ec0043' }}>← {t('back')}</a>
              </Link>
            </div>
          </>
          : registerStep === 1 ?
            <Form className='login-wrapper__form' onSubmit={handleSubmit}>
              <Form.Group controlId='name'>
                <Form.Label>{t('form.input-3.label')}:</Form.Label>
                <Form.Control
                  onChange={handleOnChange}
                  value={inputValues.name}
                  required
                  type='text'
                  autoComplete='current-text'
                  placeholder={t('form.input-3.placeholder')} />
              </Form.Group>
              <Form.Group controlId='ages'>
                <Select options={[
                    { label: t('Your age'), value: "" },
                    { label: t('From 18 to 24'), value: 0},
                    { label: t('From 25 to 34'), value: 1 },
                    { label: t('From 35 to 44'), value: 2 },
                    { label: t('From 45+'), value: 3 },
                  ]} 
                    onSelected={(ages) => setInputValues({...inputValues, ages: ages})} selected={inputValues?.ages}
                    label={t('form.input-4.label') + ':'}
                    ></Select>
              </Form.Group>
              {
                isShowCaptcha
                ?
                  <Form.Group>
                    <HCaptcha
                      sitekey={process.env.HCAPTCHA_SITE_KEY}
                      onVerify={(token,ekey) => setInputValues({...inputValues, token: token})}
                      ref={captchaRef}
                    />
                  </Form.Group>
                : <></>
              }
              <Button
                variant='primary'
                type='submit'
                disabled={isLoadingForm || isLoadingProvider}
                size='lg'
                className='form__button'>
                {isLoadingForm ? <><Spinner
                  as='span'
                  animation='border'
                  size='sm'
                  style={{ marginBottom: '2px' }}
                  role='status'
                /> {t('form.continue')}</> : <>{t('form.continue')}</>}
              </Button>
              <div className='back-link' onClick={() => backStep()}>
                ← {t('back')}
              </div>
            </Form>
            : ''}
      </div>
    </div>
  )
}

export const getStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale , ['common', 'register'], nextI18NextConfig)),
    locale: locale
  }
})

const mapStateToProps = (state) => ({
  accessToken: state.connectionReducer.accessToken,
})

export default connect(mapStateToProps)(Register)